import { useCallback, useMemo, useRef, useState } from 'react';
import Map, { Layer, Marker, Source, MapLayerMouseEvent } from 'react-map-gl/maplibre';
import type { FilterSpecification, StyleSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { usePages } from '@kevlarcon/pages';
import { StateMapData, StateProperties } from '../api/states';
import { PartnerPin } from '../api/partnerMap';
import { useThemePalette } from '../theme/useThemePalette';
import { deriveMapPalette, type MapBackground } from '../theme/deriveMapPalette';

/**
 * A style with no basemap: nothing but what's added declaratively below.
 *
 * Deliberate. There is no tile server, no API key and no external request on
 * this page — the states are the map. It also means the whole thing renders in
 * the tenant's own paper-and-brand palette instead of fighting a vendor's
 * blue-and-grey one, and it keeps working offline and in CI.
 *
 * A fixed, empty object, never recreated: react-map-gl reacts to a *new*
 * `mapStyle` by calling the underlying `setStyle()`, which throws away every
 * source/layer this component added via `<Source>`/`<Layer>` and does not
 * re-add them — so the background band (which changes live, whenever an
 * admin toggles the section's `background`) is its own `<Layer type="background">`
 * below instead of baked in here, the same way every other paint color on
 * this map already flows through an ordinary `<Layer paint>` update rather
 * than a style swap.
 *
 * Consequence worth knowing: with no `glyphs` URL, MapLibre cannot render text,
 * so the map carries no labels (partner pins are HTML <Marker>s).
 */
const EMPTY_STYLE: StyleSpecification = { version: 8, sources: {}, layers: [] };

/**
 * Three tiers, because the map draws three different things and conflating them
 * would overclaim. A state is either one this organization is registered in, one
 * it has opted into but not yet registered, or a neighbor that is only on screen
 * because it falls inside the registered footprint's bounding box.
 */
const REGISTERED = ['get', 'sgo_registered'] as FilterSpecification;
const IN_DEVELOPMENT = [
  'all', ['get', 'opted_in'], ['!', ['get', 'sgo_registered']],
] as FilterSpecification;
const NEIGHBORING = [
  'all', ['!', ['get', 'opted_in']], ['!', ['get', 'sgo_registered']],
] as FilterSpecification;

type Props = {
  data: StateMapData;
  /** Reported on hover so the caller can drive its own callout. */
  onHover?: (state: StateProperties | null) => void;
  /** Called only for registered states — the rest are context, not links. */
  onSelect?: (code: string) => void;
  /** Padding, in pixels, between the fitted bbox and the canvas edge. */
  padding?: number;
  className?: string;
  testId: string;
  ariaLabel: string;
  /** Partner pins to layer on top of the state fills — see PartnerMap.tsx
   * and docs/plans/ed-partners.md's "Page template integration" section.
   * Undefined on every caller but PartnerMap: the coverage maps
   * (`States.tsx`, `StateTemplatePage.tsx`) never show these. */
  partners?: PartnerPin[];
  /** The pin to raise and highlight — PartnerMap's results list drives this
   * so hovering a result points at its pin. */
  activePartnerId?: number | null;
  onPartnerHover?: (pin: PartnerPin | null) => void;
  /** Icon key (resolved via `PagesConfig.renderIcon`) for every pin — one
   * shared style, not per-partner. Falls back to a plain dot when unset or
   * the host app has no icon renderer configured. */
  markerIcon?: string;
  onPartnerSelect?: (pin: PartnerPin) => void;
  /** The enclosing section's chosen background band — see
   * `pages.models.Section.background`. Threaded in explicitly (rather than
   * read from CSS, the way ordinary markup gets it) because a MapLibre paint
   * property can't see `[data-section-bg]`'s custom properties — see
   * `baseStyle`'s docstring and `deriveMapPalette`. Defaults to 'none' for
   * any caller that hasn't been updated to pass it. */
  background?: MapBackground;
};

export default function StateMap({
  data,
  onHover,
  onSelect,
  padding = 24,
  className = 'state-map-canvas',
  testId,
  ariaLabel,
  partners,
  activePartnerId,
  onPartnerHover,
  markerIcon,
  onPartnerSelect,
  background = 'none',
}: Props) {
  const [hovered, setHovered] = useState<StateProperties | null>(null);
  const palette = useThemePalette();
  const mapPalette = useMemo(() => deriveMapPalette(background, palette), [background, palette]);
  const { renderIcon } = usePages();

  // The frame the server chose: cropped to where the SGO has data, or the whole
  // country. Falling back to the data's own extent would always widen it to
  // every state drawn, which is exactly what the server-side bbox prevents.
  const bounds = data.bbox;

  // The state under the cursor, mirrored in a ref so handleHover can compare
  // against it without reading `hovered` (which would recreate the handler on
  // every change) and — crucially — without calling `onHover` from inside a
  // setState updater. React runs updaters during render, so a parent's
  // setState fired from there is "update a component while rendering another".
  const hoveredRef = useRef<StateProperties | null>(null);

  const handleHover = useCallback(
    (event: MapLayerMouseEvent | null) => {
      const feature = event?.features?.[0];
      const properties = feature ? (feature.properties as unknown as StateProperties) : null;
      // mousemove fires continuously; re-rendering only when the state under
      // the cursor actually changes keeps the highlight filter from being
      // rewritten dozens of times per second.
      if (hoveredRef.current?.abbreviated_name === properties?.abbreviated_name) return;
      hoveredRef.current = properties;
      setHovered(properties);
      onHover?.(properties);
    },
    [onHover],
  );

  if (!bounds) return null;

  const canSelect = Boolean(onSelect) && Boolean(hovered?.has_page);

  return (
    <div className={className} data-testid={testId}>
      <Map
        initialViewState={{ bounds, fitBoundsOptions: { padding } }}
        mapStyle={EMPTY_STYLE}
        // A marketing page must not swallow the scroll wheel, and a north-up map
        // with no basemap has nothing to gain from rotation.
        scrollZoom={false}
        dragRotate={false}
        touchPitch={false}
        pitchWithRotate={false}
        attributionControl={false}
        interactiveLayerIds={['states-neighboring', 'states-development', 'states-registered']}
        cursor={canSelect ? 'pointer' : 'default'}
        onMouseMove={handleHover}
        onMouseOut={() => handleHover(null)}
        onClick={(event) => {
          const properties = event.features?.[0]?.properties as unknown as StateProperties | undefined;
          if (properties?.has_page) onSelect?.(properties.abbreviated_name);
        }}
        aria-label={ariaLabel}
        style={{ width: '100%', height: '100%' }}
      >
        {/* Placed before the source's layers so it's inserted beneath them —
            see EMPTY_STYLE's docstring for why this lives here rather than
            in `mapStyle` itself. */}
        <Layer id="background" type="background" paint={{ 'background-color': mapPalette.canvasBackground }} />
        <Source id="states" type="geojson" data={data}>
          <Layer
            id="states-neighboring"
            type="fill"
            filter={NEIGHBORING}
            paint={{ 'fill-color': mapPalette.neighboring }}
          />
          <Layer
            id="states-development"
            type="fill"
            filter={IN_DEVELOPMENT}
            paint={{ 'fill-color': mapPalette.developing }}
          />
          <Layer
            id="states-registered"
            type="fill"
            filter={REGISTERED}
            paint={{ 'fill-color': mapPalette.registered }}
          />
          <Layer
            id="states-hover"
            type="fill"
            // Highlight by filtering on the hovered abbreviation rather than by
            // feature-state: the source is small, this survives a re-fetch, and
            // it needs no promoteId contract with the server.
            filter={
              ['==', ['get', 'abbreviated_name'], hovered?.abbreviated_name ?? ''] as FilterSpecification
            }
            paint={{
              'fill-color': [
                'case',
                ['get', 'sgo_registered'], mapPalette.hoverRegistered,
                ['get', 'opted_in'], mapPalette.hoverDeveloping,
                mapPalette.hoverNeighboring,
              ],
            }}
          />
          <Layer
            id="states-outline"
            type="line"
            paint={{ 'line-color': mapPalette.outline, 'line-width': 0.75 }}
          />
          <Layer
            id="states-registered-outline"
            type="line"
            filter={REGISTERED}
            paint={{ 'line-color': mapPalette.registeredOutline, 'line-width': 1.25 }}
          />
        </Source>

        {partners?.map((pin) => (
          <Marker
            key={`partner-${pin.id}`}
            longitude={pin.longitude}
            latitude={pin.latitude}
            // Markers stack in DOM order; the highlighted one must not hide
            // under a neighbor.
            style={pin.id === activePartnerId ? { zIndex: 2 } : undefined}
          >
            <button
              type="button"
              className={`partner-map-pin${pin.id === activePartnerId ? ' is-active' : ''}`}
              data-testid={`partner-map-pin-${pin.id}`}
              title={pin.school_name}
              onClick={() => onPartnerSelect?.(pin)}
              onMouseEnter={() => onPartnerHover?.(pin)}
              onMouseLeave={() => onPartnerHover?.(null)}
            >
              {markerIcon && renderIcon ? renderIcon(markerIcon, 20) : <span className="partner-map-pin-dot" />}
            </button>
          </Marker>
        ))}
      </Map>
    </div>
  );
}
